class ProductBundleSelector extends HTMLElement {
  constructor() {
    super();
    this.updateButton = this.updateButton.bind(this);
    this.handleCardClick = this.handleCardClick.bind(this);
    this.scheduleUpdate = this.scheduleUpdate.bind(this);
  }

  connectedCallback() {
    this.productInformation = this.closest('product-information');
    if (!this.productInformation) return;

    this.addEventListener('change', this.updateButton);
    this.addEventListener('click', this.handleCardClick);
    this.productInformation.addEventListener('change', this.scheduleUpdate);
    this.observer = new MutationObserver(this.scheduleUpdate);
    this.observer.observe(this.productInformation, { childList: true, subtree: true });
    this.updateButton();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.updateButton);
    this.removeEventListener('click', this.handleCardClick);
    this.productInformation?.removeEventListener('change', this.scheduleUpdate);
    this.observer?.disconnect();
  }

  handleCardClick(event) {
    const card = event.target.closest('[data-cross-sell-card]');

    if (!card) {
      return;
    }

    if (
      event.target.closest('a') ||
      event.target.closest('label') ||
      event.target.closest('input')
    ) {
      return;
    }

    const checkbox = card.querySelector('[data-cross-sell-selector]');

    if (!checkbox || checkbox.disabled) {
      return;
    }

    checkbox.checked = !checkbox.checked;
    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
  }

  scheduleUpdate() {
    cancelAnimationFrame(this.updateFrame);
    this.updateFrame = requestAnimationFrame(() => this.updateButton());
  }

  getProductForm() {
    return this.productInformation.querySelector('product-form');
  }

  getSelectedVariantPrice(productForm) {
    const variantId = Number(productForm.querySelector('form input[name="id"]')?.value);
    try {
      const variants = JSON.parse(productForm.dataset.productVariants || '[]');
      return Number(variants.find((variant) => Number(variant.id) === variantId)?.price || 0);
    } catch (error) {
      return 0;
    }
  }

  formatMoney(cents, format) {
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const moneyFormat = format || '${{amount}}';
    const formatNumber = (value, precision = 2, thousands = ',', decimal = '.') => {
      const number = (Number(value) / 100).toFixed(precision);
      const parts = number.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
    };
    const token = moneyFormat.match(placeholder)?.[1] || 'amount';
    const values = {
      amount: formatNumber(cents),
      amount_no_decimals: formatNumber(cents, 0),
      amount_with_comma_separator: formatNumber(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: formatNumber(cents, 0, '.', ','),
      amount_with_space_separator: formatNumber(cents, 2, ' ', ','),
      amount_no_decimals_with_space_separator: formatNumber(cents, 0, ' '),
    };
    return moneyFormat.replace(placeholder, values[token] ?? values.amount);
  }

  updateButton() {
    const productForm = this.getProductForm();
    const button = productForm?.querySelector('.product--add-to-cart-button');
    const label = button?.querySelector('[data-add-to-cart-text]');
    if (!productForm || !button || !label || button.disabled) return;

    const extrasTotal = Array.from(
      this.productInformation.querySelectorAll('[data-cross-sell-selector]:checked')
    ).reduce((total, input) => total + Number(input.dataset.crossSellPrice || 0), 0);
    const total = this.getSelectedVariantPrice(productForm) + extrasTotal;
    const buttonLabel = button.dataset.bundleButtonLabel || label.textContent.split(' - ')[0].trim();
    const nextText = `${buttonLabel} - ${this.formatMoney(total, productForm.dataset.moneyFormat)}`;

    if (label.textContent.trim() !== nextText) {
      label.textContent = nextText;
    }
  }
}

if (!customElements.get('product-bundle-selector')) {
  customElements.define('product-bundle-selector', ProductBundleSelector);
}
